import { Clock, User, ShoppingBag } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/utils'

const STATUS_COLORS: Record<string, string> = {
  CREATED: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-orange-100 text-orange-800',
  PAID: 'bg-blue-100 text-blue-800',
  OUT_FOR_DELIVERY: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-purple-100 text-purple-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  RETURNED: 'bg-gray-100 text-gray-800',
}

const STATUS_LABELS: Record<string, string> = {
  CREATED: 'Created',
  CONFIRMED: 'Confirmed',
  PAID: 'Confirmed',
  PREPARING: 'Preparing',
  SHIPPED: 'Shipped',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURN_REQUESTED: 'Return Requested',
  RETURNED: 'Returned',
}

interface OrderCardProps {
  order: {
    id: string
    orderNumber?: string
    status: string
    totalAmount: number
    orderType: string
    createdAt: string
    items?: { name?: string; productName?: string; quantity: number }[]
    customer?: { account?: { name: string; phone: string } }
  }
  onViewDetail: () => void
  onAccept?: () => void
}

export function OrderCard({ order, onViewDetail, onAccept }: OrderCardProps) {
  const itemCount = order.items?.length ?? 0
  const customerName = order.customer?.account?.name ?? 'Customer'
  const isNew = order.status === 'CREATED'

  return (
    <div className={cn(
      'bg-card border rounded-xl p-4 hover:shadow-md transition-shadow',
      isNew && 'border-yellow-300 bg-yellow-50/30'
    )}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-foreground">
              #{order.orderNumber || order.id.slice(0, 8)}
            </p>
            <Badge variant="outline" className="text-xs">
              {order.orderType}
            </Badge>
          </div>
          <Badge className={cn('text-xs', STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800')}>
            {STATUS_LABELS[order.status] || order.status.replace(/_/g, ' ')}
          </Badge>
        </div>
        <p className="text-lg font-bold text-foreground">
          ₹{order.totalAmount.toFixed(2)}
        </p>
      </div>

      <div className="space-y-1.5 mb-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5" />
          <span>{customerName}</span>
        </div>
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-3.5 h-3.5" />
          <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          <span>{new Date(order.createdAt).toLocaleString()}</span>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onViewDetail}>
          View Details
        </Button>
        {isNew && onAccept && (
          <Button size="sm" className="flex-1" onClick={onAccept}>
            Accept Order
          </Button>
        )}
      </div>
    </div>
  )
}
